class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();

    // Start loading the product list in the background so the first search is fast
    PredictiveSearch.loadProductIndex();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText && previousTerm) {
      // Escape special characters so terms like "(" or "+" don't break the regex
      const escaped = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const matches = currentButtonText.match(new RegExp(escaped, 'g'));
      if (!matches || matches.length > 1) {
        // The term is missing or matches part of the button text, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const isAbort = (error) => error?.name === 'AbortError' || error?.code === 20;

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        return section ? section.innerHTML : '';
      })
      // If Shopify's search fails, still show our own matches instead of nothing
      .catch((error) => {
        if (isAbort(error)) throw error;
        return '';
      })
      .then((shopifyMarkup) => this.buildResultsMarkup(shopifyMarkup, searchTerm))
      .then((resultsMarkup) => {
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        // Ignore responses for a term the shopper has already typed past
        if (searchTerm !== this.searchTerm) return;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (isAbort(error)) return;
        this.close();
        console.error(error);
      });
  }

  /* ---------------------------------------------------------------------------
   * Fallback matching
   * Shopify's predictive search can miss products (stale index, hidden from
   * search, etc.). We also match the typed words against every product title,
   * add any products Shopify missed, and show a "no results" message when
   * nothing matches at all.
   * ------------------------------------------------------------------------- */

  static loadProductIndex() {
    if (!PredictiveSearch.productIndexPromise) {
      PredictiveSearch.productIndexPromise = (async () => {
        const root = window.Shopify?.routes?.root || '/';
        const all = [];
        for (let page = 1; page <= 10; page++) {
          const response = await fetch(`${root}products.json?limit=250&page=${page}`);
          if (!response.ok) break;
          const data = await response.json();
          const products = data?.products || [];
          if (!products.length) break;
          products.forEach((product) => {
            all.push({
              handle: product.handle,
              title: product.title || '',
              tags: Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(','),
              type: product.product_type || '',
              image: product.images?.[0] || null,
            });
          });
          if (products.length < 250) break;
        }
        return all;
      })().catch(() => []);
    }
    return PredictiveSearch.productIndexPromise;
  }

  static toWords(text) {
    return (text || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .split(/[^a-z0-9]+/)
      .filter(Boolean);
  }

  static matchProducts(products, searchTerm) {
    const queryWords = PredictiveSearch.toWords(searchTerm);
    if (!queryWords.length) return [];

    return products
      .map((product) => {
        const titleWords = PredictiveSearch.toWords(product.title);
        const titleJoined = titleWords.join(' ');
        const keywordWords = PredictiveSearch.toWords([...product.tags, product.type].join(' '));
        let score = 0;

        queryWords.forEach((word) => {
          if (titleWords.some((t) => t.startsWith(word))) {
            score += 3; // "rain" -> "Rainwood"
          } else if (word.length >= 3 && titleJoined.includes(word)) {
            score += 2; // "wood" -> "Rainwood"
          } else if (keywordWords.some((k) => k.startsWith(word))) {
            score += 1; // matches a tag or product type
          }
        });

        return { product, score };
      })
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((entry) => entry.product);
  }

  static handleFromUrl(href) {
    try {
      const path = new URL(href, window.location.origin).pathname;
      const match = path.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : href;
    } catch (e) {
      return href;
    }
  }

  async buildResultsMarkup(shopifyMarkup, searchTerm) {
    const MAX_PRODUCTS = 10;
    const doc = document.implementation.createHTMLDocument('');
    const container = doc.createElement('div');
    container.innerHTML = shopifyMarkup || '';

    let results = container.querySelector('#predictive-search-results');
    if (!results) {
      container.innerHTML = '';
      results = doc.createElement('div');
      results.id = 'predictive-search-results';
      results.setAttribute('role', 'listbox');
      container.appendChild(results);
    }

    let liveRegion = container.querySelector('[data-predictive-search-live-region-count-value]');
    if (!liveRegion) {
      liveRegion = doc.createElement('span');
      liveRegion.className = 'hidden';
      liveRegion.setAttribute('data-predictive-search-live-region-count-value', '');
      container.appendChild(liveRegion);
    }

    let list = results.querySelector('#predictive-search-results-products-list');
    const existingHandles = new Set(
      list
        ? Array.from(list.querySelectorAll('a[href]')).map((a) => PredictiveSearch.handleFromUrl(a.getAttribute('href')))
        : []
    );

    const products = await PredictiveSearch.loadProductIndex();
    const extraProducts = PredictiveSearch.matchProducts(products, searchTerm)
      .filter((product) => !existingHandles.has(product.handle))
      .slice(0, Math.max(0, MAX_PRODUCTS - existingHandles.size));

    if (extraProducts.length) {
      if (!list) list = this.createProductsGroup(doc, results);
      const offset = list.children.length;
      extraProducts.forEach((product, i) => {
        list.appendChild(this.createProductItem(doc, product, offset + i + 1));
      });
    }

    const totalItems = results.querySelectorAll('li[role="option"]').length;

    if (totalItems === 0) {
      const message = doc.createElement('p');
      message.className = 'predictive-search__no-results';
      message.textContent = this.getNoResultsText(searchTerm);
      results.prepend(message);
      liveRegion.textContent = message.textContent;
    } else if (extraProducts.length) {
      liveRegion.textContent = `${totalItems} ${this.dataset.productsHeading || 'Products'}`;
    }

    return container.innerHTML;
  }

  createProductsGroup(doc, results) {
    let wrapper = results.querySelector('#predictive-search-results-groups-wrapper');
    if (!wrapper) {
      wrapper = doc.createElement('div');
      wrapper.id = 'predictive-search-results-groups-wrapper';
      wrapper.className =
        'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions';
      results.prepend(wrapper);
    }
    wrapper.classList.remove('predictive-search__results-groups-wrapper--no-products');

    const group = doc.createElement('div');
    group.className = 'predictive-search__result-group';
    const inner = doc.createElement('div');

    const heading = doc.createElement('h2');
    heading.id = 'predictive-search-products';
    heading.className = 'predictive-search__heading text-body caption-with-letter-spacing';
    heading.textContent = this.dataset.productsHeading || 'Products';

    const list = doc.createElement('ul');
    list.id = 'predictive-search-results-products-list';
    list.className = 'predictive-search__results-list list-unstyled';
    list.setAttribute('role', 'group');
    list.setAttribute('aria-labelledby', 'predictive-search-products');

    inner.append(heading, list);
    group.appendChild(inner);
    wrapper.appendChild(group);
    return list;
  }

  createProductItem(doc, product, index) {
    const root = window.Shopify?.routes?.root || '/';

    const item = doc.createElement('li');
    item.id = `predictive-search-option-product-${index}`;
    item.className = 'predictive-search__list-item';
    item.setAttribute('role', 'option');
    item.setAttribute('aria-selected', 'false');

    const link = doc.createElement('a');
    link.href = `${root}products/${encodeURIComponent(product.handle)}`;
    link.className = 'predictive-search__item predictive-search__item--link-with-thumbnail link link--text';
    link.tabIndex = -1;

    if (product.image?.src) {
      const img = doc.createElement('img');
      img.className = 'predictive-search__image';
      try {
        const src = new URL(product.image.src, window.location.origin);
        src.searchParams.set('width', '150');
        img.src = src.toString();
      } catch (e) {
        img.src = product.image.src;
      }
      img.alt = product.image.alt || '';
      img.width = 50;
      img.height =
        product.image.width && product.image.height
          ? Math.round((50 * product.image.height) / product.image.width)
          : 50;
      img.loading = 'lazy';
      link.appendChild(img);
    }

    const content = doc.createElement('div');
    content.className = 'predictive-search__item-content predictive-search__item-content--centered';
    const title = doc.createElement('p');
    title.className = 'predictive-search__item-heading h5';
    title.textContent = product.title;
    content.appendChild(title);
    link.appendChild(content);

    item.appendChild(link);
    return item;
  }

  getNoResultsText(searchTerm) {
    const template = this.dataset.noResultsText;
    return template ? template.replace('__TERMS__', searchTerm) : `No results found for “${searchTerm}”`;
  }

  /* ------------------------------------------------------------------------- */

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countElement = this.querySelector('[data-predictive-search-live-region-count-value]');
    this.setLiveRegionText(countElement ? countElement.textContent : '');
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);